import React, { useState, useEffect } from 'react';
import './styles/AboutContact.css';
import { sendEmail } from '../../services/emailService';

const AboutContact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    website: '' // honeypot field
  });

  const [submitStatus, setSubmitStatus] = useState({
    success: false,
    error: null
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    // Check if URL has #contact hash and scroll to contact section
    if (window.location.hash === '#contact') {
      const contactSection = document.getElementById('contact');
      if (contactSection) {
        contactSection.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, []); // Empty dependency array means this runs once when component mounts

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear any previous status messages when user starts typing
    if (submitStatus.success || submitStatus.error) {
      setSubmitStatus({ success: false, error: null });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check honeypot - if filled, silently reject
    if (formData.website) {
      setFormData({ name: '', email: '', subject: '', message: '', website: '' });
      return;
    }

    setIsSubmitting(true);
    try {
      await sendEmail(formData);
      setSubmitStatus({ success: true, error: null });
      setFormData({ name: '', email: '', subject: '', message: '', website: '' });
    } catch (error) {
      console.error('Failed to send message:', error);
      setSubmitStatus({ success: false, error: 'Failed to send message. Please try again later.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="about-contact-container">
      <section className="about-section">
        <div className="mission-statement">
          <h2>About Pre Go Prep</h2>
          <p>
            This tool was built by barrel racers who know exactly what it's like to juggle therapy schedules, 
            draw numbers, and the chaos of race day, all while wondering if you used your tools a few minutes 
            too early… or too late.
          </p>
          
          <p>
            It's made for that moment when you're staring at your draw, trying to work backward from a rough 
            start time, while managing a trailer full of horses and a head full of logistics.
          </p>
          
          <p>
            This calculator takes the mental math off your plate. It figures out when to apply therapies and 
            gives you an estimated go time. So you can stay focused on riding and winning!
          </p>
          
          <p>
            Designed with real-world experience in mind, it's fast, mobile-friendly, and built to help you 
            show up ready!
          </p>
        </div>

        <div className="mission-statement" style={{ marginTop: '2rem' }}>
          <h2>Our Mission</h2>
          <p>
            Our mission is to help barrel racers perform at their best by eliminating 
            the guesswork of race-day prep. With smart, easy-to-use timing tools, we 
            take the stress out of scheduling so you can focus on what matters most, 
            your performance go!
          </p>
        </div>
      </section>

      <section className="contact-section" id="contact">
        <div className="mission-statement">
          <h2>Contact Us</h2>
          <p className="contact-intro">
            Have questions or feedback? We'd love to hear from you. Fill out the form below and we'll get back to you as soon as possible.
          </p>
          
          <form onSubmit={handleSubmit} className="contact-form">
            <div className="form-group">
              <label htmlFor="name">Name</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                placeholder="Your name"
              />
            </div>

            {/* Honeypot field - hidden from real users */}
            <input
              type="text"
              name="website"
              value={formData.website}
              onChange={handleInputChange}
              style={{ display: 'none' }}
              tabIndex="-1"
              aria-hidden="true"
            />

            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                placeholder="Your email"
              />
            </div>

            <div className="form-group">
              <label htmlFor="subject">Subject</label>
              <input
                type="text"
                id="subject"
                name="subject"
                value={formData.subject}
                onChange={handleInputChange}
                required
                placeholder="What's this about?"
              />
            </div>

            <div className="form-group">
              <label htmlFor="message">Message</label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                required
                placeholder="Your message"
                rows="5"
              />
            </div>

            <button 
              type="submit" 
              className="submit-button"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Sending...' : 'Send Message'}
            </button>
            
            {submitStatus.success && (
              <p className="success-message">
                Thank you for your message! We will get back to you soon.
              </p>
            )}
            {submitStatus.error && (
              <p className="error-message">
                {submitStatus.error}
              </p>
            )}
          </form>
        </div>
      </section>
    </div>
  );
};

export default AboutContact; 